import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCaretDown, faCaretUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { PropsWithChildren } from 'react'
import { Link } from 'react-router-dom'
import { useRouter } from '../../hooks/useRouter'
import { Popover } from '../Popover/Popover'
import { usePopper } from '../Popover/hooks/usePopper'
import { INavItemProps, NavItem } from './NavItem'

interface IProps {
  value?: INavItemProps
  options?: INavItemProps[]
  onClick?: (value: INavItemProps) => Promise<void> | void
}

export const NavTabs = ({
  value,
  options = [],
  onClick: handleClick,
}: PropsWithChildren<IProps>): React.ReactElement => {
  const router = useRouter()
  const selected: INavItemProps | undefined = value || options.find((i) => i.to === router.pathname)
  const { toggle, isOpen, target, targetProps, ...tooltipProps } = usePopper<HTMLButtonElement>({
    placement: 'bottom-start',
  })
  const icon: IconProp = isOpen ? faCaretUp : faCaretDown
  return (
    <>
      <ul className="nav nav-header nav-tabs nav-overflow header-tabs d-none d-md-flex border-bottom">
        {options.map(
          (item: INavItemProps): React.ReactElement => (
            <NavItem
              key={item.id}
              {...item}
              active={selected && selected?.id === item.id}
              onClick={async () => {
                await item.onClick?.(item)
                await handleClick?.(item)
              }}
            />
          ),
        )}
      </ul>
      <div className="d-block d-md-none mt-2">
        <button
          ref={target}
          {...targetProps}
          type="button"
          className={[
            'btn btn-outline-primary btn-block d-flex nav-toggle w-100',
            isOpen ? 'open' : '',
          ]
            .join(' ')
            .trim()}
          onClick={() => toggle()}
        >
          <span>{selected?.children || 'Navigate to...'}</span>
          <span className="ms-auto">
            <FontAwesomeIcon icon={icon} />
          </span>
        </button>
        <div>
          <Popover
            {...tooltipProps}
            toggle={toggle}
            isOpen={isOpen}
            className="w-100"
            bodyProps={{ style: { position: 'static', top: 0, left: 0, display: 'block' } }}
          >
            <div className="list-group">
              {options.map((item: INavItemProps): React.ReactElement => {
                const navLinkClassNames: string[] = [
                  'list-group-item d-flex align-items-center',
                  item.className || '',
                ]
                if (item.disabled) navLinkClassNames.push('disabled')
                if (item.active || item.id === value?.id) navLinkClassNames.push('active')
                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    className={navLinkClassNames.filter(Boolean).join(' ').trim()}
                    onClick={async () => {
                      await item.onClick?.(item)
                      await handleClick?.(item)
                      toggle()
                    }}
                  >
                    {item.children}
                  </Link>
                )
              })}
            </div>
          </Popover>
          {isOpen ? <div className="modal-backdrop fade show"></div> : null}
        </div>
      </div>
    </>
  )
}
